import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Lightbox from '../Lightbox/Lightbox';
import { contentService } from '../../services/api';
import { IMAGE_FILE, sha256Hex, shrinkImage } from '../../utils/imagePrep';
import './PhotoLibrary.css';

const KINDS = [
  { key: 'all', label: 'All' },
  { key: 'unsorted', label: 'Not sorted' },
  { key: 'before', label: 'Before' },
  { key: 'after', label: 'After' },
  { key: 'paired', label: 'Pairs' },
  { key: 'us', label: 'Us' },
  { key: 'other', label: 'Other' }
];
const KIND_LABEL = { unsorted: 'Not sorted', before: 'Before', after: 'After', us: 'Us', other: 'Other' };
const FLAG = { house_number: 'house number', number_plate: 'number plate', face: 'face', street_sign: 'street sign' };
const PAGE = 120;
// Phones and tablets pick from the gallery: a folder picker there does nothing useful.
const isTouchDevice = () => typeof window !== 'undefined'
  && (window.matchMedia?.('(any-hover: none)').matches || (navigator.maxTouchPoints > 0 && window.matchMedia?.('(max-width: 900px)').matches));

function flagsOf(ai) {
  if (!ai?.privacy) return [];
  const f = Object.entries(ai.privacy).filter(([k, v]) => k !== 'other' && v === true).map(([k]) => FLAG[k] || k);
  if (ai.privacy.other) f.push(ai.privacy.other);
  return f;
}

/**
 * Our photo library (Content Studio, GID-Session-025): photos loaded from our own
 * computers and phones. Manage mode (the Content page's Photos tab) sorts, pairs,
 * deletes and starts posts; pick mode (inside the photo picker) adds to a post.
 *   pickMode, onAdd(libraryIds), onBeforeAfter({library}, {library}), onNewPost(libraryIds)
 */
export default function PhotoLibrary({ pickMode = false, onAdd, onBeforeAfter, onNewPost, aiReady = true }) {
  const [kind, setKind] = useState('all');
  const [data, setData] = useState(null); // { photos, counts, total }
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [up, setUp] = useState(null); // upload progress
  const [batchKind, setBatchKind] = useState('');
  const [batchSuburb, setBatchSuburb] = useState('');
  const [sorting, setSorting] = useState(null); // AI sort progress
  const [suburb, setSuburb] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const cancelRef = useRef(false);
  const filesRef = useRef(null);
  const folderRef = useRef(null);

  const load = useCallback(async (k = kind, more = false) => {
    const r = await contentService.library({ kind: k === 'all' ? undefined : k, limit: PAGE, offset: more ? (data?.photos.length || 0) : 0 });
    if (!r.success) return setError(r.error);
    setData((d) => (more && d ? { ...r, photos: [...d.photos, ...r.photos] } : r));
  }, [kind, data?.photos.length]);

  useEffect(() => { load(kind); setSelected([]); setConfirmDelete(false); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const photos = data?.photos || [];
  const byId = useMemo(() => new Map(photos.map((p) => [p.id, p])), [photos]);
  const sel = selected.map((id) => byId.get(id)).filter(Boolean);
  const neverLooked = data?.counts?.not_checked || 0;
  const pairSel = (() => {
    if (sel.length === 2) {
      const b = sel.find((p) => p.kind === 'before');
      const a = sel.find((p) => p.kind === 'after');
      if (b && a) return { b, a };
    }
    if (sel.length === 1 && sel[0].pair_id) {
      const other = byId.get(sel[0].pair_id);
      if (other) return sel[0].kind === 'before' ? { b: sel[0], a: other } : { b: other, a: sel[0] };
    }
    return null;
  })();

  // ---- upload ----
  async function uploadFiles(fileList) {
    const files = [...fileList].filter((f) => IMAGE_FILE.test(f.name) || f.type.startsWith('image/'));
    if (!files.length) return setError('No photos in that selection (JPEG, PNG, WebP or HEIC).');
    setError('');
    cancelRef.current = false;
    const state = { total: files.length, done: 0, added: 0, dupes: 0, failed: [] };
    setUp({ ...state });
    for (const f of files) {
      if (cancelRef.current) break;
      try {
        const [hash, small] = await Promise.all([sha256Hex(f), shrinkImage(f)]);
        const r = await contentService.uploadLibrary(small, {
          hash, taken_at: f.lastModified ? new Date(f.lastModified).toISOString() : undefined,
          kind: batchKind || undefined, suburb: batchSuburb || undefined
        });
        if (!r.success) state.failed.push(r.error || f.name);
        else if (r.duplicate) state.dupes++;
        else state.added++;
      } catch (e) {
        state.failed.push(`${f.name}: ${e.message}`);
      }
      state.done++;
      setUp({ ...state });
    }
    setUp({ ...state, finished: true, cancelled: cancelRef.current });
    await load(kind);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  }

  // ---- AI sort ----
  async function sortAi(ids) {
    setSorting({ done: 0, total: ids?.length || neverLooked, batch: 0, batches: 0 }); setError('');
    const r = await contentService.pollAi(contentService.sortLibrary(ids), { timeoutMs: 30 * 60 * 1000, intervalMs: 2500, onProgress: setSorting });
    setSorting(null);
    if (!r.success) return setError(r.error);
    setUp((u) => (u ? { ...u, sorted: r.result } : { finished: true, sorted: r.result }));
    await load(kind);
  }

  // ---- selection actions ----
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  async function run(promise, keepSelection = false) {
    setBusy(true); setError('');
    const r = await promise;
    setBusy(false);
    if (!r.success) { setError(r.error); return false; }
    if (!keepSelection) setSelected([]);
    setConfirmDelete(false);
    await load(kind);
    return true;
  }
  const setKindFor = (k) => run(contentService.updateLibrary(selected, { kind: k }), true);
  const setSuburbFor = () => run(contentService.updateLibrary(selected, { suburb }), true);
  const pair = () => pairSel && run(contentService.pairLibrary(pairSel.b.id, pairSel.a.id), true);
  const unpair = () => run(Promise.all(sel.filter((p) => p.pair_id).map((p) => contentService.unpairLibrary(p.id))).then((rs) => rs.find((x) => !x.success) || { success: true }), true);
  const remove = () => run(contentService.deleteLibrary(selected));

  async function add() {
    setBusy(true);
    await onAdd?.(selected);
    setBusy(false);
  }
  async function makeBa() {
    setBusy(true);
    await onBeforeAfter?.({ library: pairSel.b.id }, { library: pairSel.a.id });
    setBusy(false);
  }

  const counts = data?.counts || {};
  return (
    <div className={`photo-library${pickMode ? ' pick' : ''}${selected.length ? ' has-sel' : ''}`}>
      <div className={`pl-drop${dragOver ? ' over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
        <div className="pl-drop-main">
          <strong>Load photos from this {isTouchDevice() ? 'phone' : 'computer'}</strong>
          <span className="text-muted">Drop them here, or pick photos or a whole folder. Same photo twice? It's skipped. Only add photos that are ours to post.</span>
        </div>
        <div className="pl-drop-actions">
          <button type="button" className="btn btn-primary btn-sm" disabled={!!up && !up.finished} onClick={() => filesRef.current?.click()}>Choose photos</button>
          {!isTouchDevice() && <button type="button" className="btn btn-outline btn-sm pl-folder" disabled={!!up && !up.finished} onClick={() => folderRef.current?.click()}>Choose a folder</button>}
          <input ref={filesRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple hidden onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
        </div>
        {!pickMode && (
          <div className="pl-drop-fields">
            <label>These are
              <select value={batchKind} onChange={(e) => setBatchKind(e.target.value)}>
                <option value="">for the AI to sort</option>
                <option value="before">befores</option>
                <option value="after">afters</option>
                <option value="us">us / behind the scenes</option>
                <option value="other">other</option>
              </select>
            </label>
            <label>Suburb <input value={batchSuburb} onChange={(e) => setBatchSuburb(e.target.value)} placeholder="optional" /></label>
          </div>
        )}
      </div>

      {up && (
        <div className="pl-progress" data-finished={up.finished ? '1' : '0'}>
          {!up.finished ? (
            <>
              <div className="pl-bar"><span style={{ width: `${Math.round((up.done / up.total) * 100)}%` }} /></div>
              <span>Loading {up.done} of {up.total}… {up.dupes ? `(${up.dupes} already in)` : ''}</span>
              <button type="button" className="btn-link" onClick={() => { cancelRef.current = true; }}>Stop</button>
            </>
          ) : (
            <span>
              {up.cancelled ? 'Stopped. ' : ''}{up.added != null && `Added ${up.added} photo${up.added === 1 ? '' : 's'}`}{up.dupes ? `, ${up.dupes} already in` : ''}{up.failed?.length ? `, ${up.failed.length} couldn't be read` : ''}.
              {up.sorted && ` The AI looked at ${up.sorted.looked_at}: sorted ${up.sorted.sorted}, paired ${up.sorted.paired}.`}
              <button type="button" className="btn-link" onClick={() => setUp(null)}> OK</button>
            </span>
          )}
          {up.failed?.length > 0 && up.finished && <ul className="pl-failed">{up.failed.slice(0, 6).map((f, i) => <li key={i}>{f}</li>)}</ul>}
        </div>
      )}

      {aiReady && (neverLooked > 0 || sorting) && (
        <div className="pl-ai">
          {sorting ? (
            <span><span className="loading-spinner small"></span> {sorting.pairing ? 'Matching befores with their afters…' : `The AI is sorting… ${sorting.done} of ${sorting.total}${sorting.batches ? ` (batch ${sorting.batch} of ${sorting.batches})` : ''}`}</span>
          ) : (
            <>
              <span>{neverLooked} photo{neverLooked === 1 ? " hasn't" : "s haven't"} been looked at yet.</span>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => sortAi()}>✨ Sort with AI</button>
              <span className="text-muted">Before, after, us or other; what each shows; privacy flags; pairs. About a cent per 50 photos.</span>
            </>
          )}
        </div>
      )}

      <div className="pl-filters">
        {KINDS.map((k) => (
          <button key={k.key} type="button" className={`pe-tweak${kind === k.key ? ' on' : ''}`} onClick={() => setKind(k.key)}>
            {k.label}{counts[k.key] != null ? ` (${k.key === 'paired' ? Math.floor(counts[k.key] / 2) : counts[k.key]})` : ''}
          </button>
        ))}
      </div>

      {error && <div className="form-error">{error}</div>}

      {!data ? <div className="pl-empty"><div className="loading-spinner"></div></div> : photos.length === 0 ? (
        <div className="pl-empty"><p>{kind === 'all' ? 'No photos yet. Load some from your computer or phone above.' : 'None here.'}</p></div>
      ) : (
        <div className="pl-grid">
          {photos.map((p, i) => {
            const flags = flagsOf(p.ai);
            const on = selected.includes(p.id);
            return (
              <div key={p.id} className={`pl-item${on ? ' on' : ''}`} data-lib={p.id}>
                <button type="button" className="pl-thumb" onClick={() => toggle(p.id)} title={p.ai?.shows || p.original_name || ''}>
                  <img src={contentService.libraryUrl(p.id, 'thumb')} alt={p.ai?.shows || p.original_name || 'photo'} loading="lazy" />
                  <span className={`pl-kind kind-${p.kind}`}>{KIND_LABEL[p.kind]}{p.pair_id ? ' 🔗' : ''}</span>
                  {on && <span className="pp-tick">✓</span>}
                  {p.ai?.quality ? <span className="pp-quality">{'★'.repeat(Math.max(1, Math.min(5, p.ai.quality)))}</span> : null}
                  {flags.length > 0 && <span className="pp-flag" title={`Shows a ${flags.join(', ')}`}>⚠ {flags.join(', ')}</span>}
                </button>
                <div className="pl-meta">
                  <span className="pl-caption">{p.ai?.shows || p.original_name}</span>
                  <button type="button" className="pl-zoom" title="View large" onClick={() => setLightbox({ images: photos.map((x) => ({ src: contentService.libraryUrl(x.id), label: x.ai?.shows || x.original_name || '' })), index: i })}>⤢</button>
                </div>
                {(p.suburb || p.pair_id) && <span className="pl-sub text-muted">{[p.suburb, p.pair_id ? `pair with ${KIND_LABEL[byId.get(p.pair_id)?.kind] || 'photo'} #${p.pair_id}` : ''].filter(Boolean).join(' · ')}</span>}
              </div>
            );
          })}
        </div>
      )}
      {data && photos.length < data.total && (
        <button type="button" className="btn btn-secondary pl-more" onClick={() => load(kind, true)}>Show more ({data.total - photos.length} to go)</button>
      )}

      {selected.length > 0 && (
        <div className="pl-selbar">
          <span className="pl-selcount">{selected.length} picked <button type="button" className="btn-link" onClick={() => setSelected([])}>clear</button></span>
          {pickMode ? (
            <>
              {pairSel && <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={makeBa}>Make a before/after image</button>}
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={add}>Add {selected.length} to the post</button>
            </>
          ) : (
            <>
              <span className="pl-setkind">Set as:
                {['before', 'after', 'us', 'other'].map((k) => <button key={k} type="button" className="pe-tweak" disabled={busy} onClick={() => setKindFor(k)}>{KIND_LABEL[k]}</button>)}
              </span>
              <span className="pl-setsuburb">
                <input value={suburb} onChange={(e) => setSuburb(e.target.value)} placeholder="Suburb" />
                <button type="button" className="pe-tweak" disabled={busy} onClick={setSuburbFor}>Set</button>
              </span>
              {sel.length === 2 && pairSel && !(pairSel.b.pair_id === pairSel.a.id) && <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={pair}>Pair them</button>}
              {sel.some((p) => p.pair_id) && <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={unpair}>Unpair</button>}
              {aiReady && <button type="button" className="btn btn-outline btn-sm" disabled={busy || !!sorting} onClick={() => sortAi(selected)}>✨ Look again</button>}
              {onNewPost && <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => onNewPost(selected)}>New post with {selected.length === 1 ? 'this' : 'these'}</button>}
              {confirmDelete
                ? <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={remove}>Yes, delete {selected.length}</button>
                : <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(true)}>Delete</button>}
            </>
          )}
        </div>
      )}
      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}
